import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, Pencil, Upload, Loader2, BookOpen, Layers, Image as ImageIcon,
  ArrowUp, ArrowDown, ExternalLink, Shield, ChevronLeft, Eye, Mail, Star, RefreshCw,
} from "lucide-react";
import { SubscribersDialog } from "../components/SubscribersDialog";
import api, { resolveImg, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge } from "../components/StatusBadge";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

/* ---------- Image upload field ---------- */
function ImageUploadField({ label, value, onChange, testid }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <Label className="text-gray-300 text-sm">{label}</Label>
      <div className="mt-1.5 flex items-center gap-3">
        <div className="w-16 h-16 rounded-lg bg-[#0B0C10] border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
          {value ? (
            <img src={resolveImg(value)} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="w-5 h-5 text-gray-600" />
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleFile} data-testid={`${testid}-file`} />
        <Button
          type="button"
          data-testid={`${testid}-btn`}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          variant="outline"
          className="border-white/15 bg-transparent text-gray-200 hover:bg-white/5"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {value ? "Replace" : "Upload"}
        </Button>
      </div>
    </div>
  );
}

const EMPTY_SERIES = { title: "", synopsis: "", genre: "", author: "", status: "Ongoing", cover_image_url: "", banner_image_url: "", featured_tagline: "" };

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  const [seriesList, setSeriesList] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState(null);
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subsOpen, setSubsOpen] = useState(false);
  const [replacingId, setReplacingId] = useState(null);

  // dialogs
  const [seriesDialog, setSeriesDialog] = useState(false);
  const [seriesForm, setSeriesForm] = useState(EMPTY_SERIES);
  const [editingSeriesId, setEditingSeriesId] = useState(null);
  const [savingSeries, setSavingSeries] = useState(false);

  const [chapterDialog, setChapterDialog] = useState(false);
  const [chapterForm, setChapterForm] = useState({ chapter_number: "", title: "", is_published: true });
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [savingChapter, setSavingChapter] = useState(false);

  const panelInputRef = useRef(null);
  const [uploadingPanel, setUploadingPanel] = useState(false);

  const loadSeries = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/series");
      setSeriesList(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSeries(); }, []);

  const openSeriesDetail = async (s) => {
    setSelectedSeries(s);
    setSelectedChapter(null);
    setPanels([]);
    const { data } = await api.get(`/admin/series/${s.id}/chapters`);
    setChapters(data);
  };

  const refreshChapters = async (seriesId) => {
    const { data } = await api.get(`/admin/series/${seriesId}/chapters`);
    setChapters(data);
  };

  const openChapterPanels = async (c) => {
    setSelectedChapter(c);
    const { data } = await api.get(`/admin/chapters/${c.id}/panels`);
    setPanels(data);
  };

  /* ----- Series CRUD ----- */
  const openNewSeries = () => { setSeriesForm(EMPTY_SERIES); setEditingSeriesId(null); setSeriesDialog(true); };
  const featureSeries = async (s) => {
    try {
      await api.post(`/admin/series/${s.id}/feature`);
      toast.success(`${s.title} is now featured on the homepage`);
      setSeriesList((list) => list.map((x) => ({ ...x, is_featured: x.id === s.id })));
      setSelectedSeries({ ...s, is_featured: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const openEditSeries = (s) => {
    setSeriesForm({ title: s.title, synopsis: s.synopsis, genre: s.genre, author: s.author, status: s.status, cover_image_url: s.cover_image_url, banner_image_url: s.banner_image_url, featured_tagline: s.featured_tagline || "" });
    setEditingSeriesId(s.id);
    setSeriesDialog(true);
  };
  const saveSeries = async () => {
    if (!seriesForm.title.trim()) return toast.error("Title is required");
    setSavingSeries(true);
    try {
      if (editingSeriesId) {
        await api.put(`/admin/series/${editingSeriesId}`, seriesForm);
        toast.success("Series updated");
      } else {
        await api.post("/admin/series", seriesForm);
        toast.success("Series created");
      }
      setSeriesDialog(false);
      await loadSeries();
      if (selectedSeries && editingSeriesId === selectedSeries.id) {
        const { data } = await api.get("/admin/series");
        setSelectedSeries(data.find((x) => x.id === editingSeriesId) || null);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingSeries(false);
    }
  };
  const deleteSeries = async (s) => {
    if (!window.confirm(`Delete "${s.title}" and all its chapters/panels?`)) return;
    try {
      await api.delete(`/admin/series/${s.id}`);
      toast.success("Series deleted");
      if (selectedSeries?.id === s.id) { setSelectedSeries(null); setChapters([]); }
      loadSeries();
    } catch (err) {
      toast.error("Delete failed");
    }
  };
  const toggleSeriesPublish = async (s) => {
    try {
      const { data } = await api.post(`/admin/series/${s.id}/publish`, { is_published: !s.is_published });
      toast.success(data.is_published ? "Series published — now live" : "Series moved to Draft");
      if (selectedSeries?.id === s.id) setSelectedSeries({ ...selectedSeries, is_published: data.is_published });
      loadSeries();
    } catch {
      toast.error("Failed to update publish state");
    }
  };
  const openPreview = (chapterNumber) => {
    if (chapterNumber === undefined || chapterNumber === null) {
      toast.error("No chapters to preview yet");
      return;
    }
    window.open(`/series/${selectedSeries.slug}/chapter/${chapterNumber}?preview=1`, "_blank");
  };

  /* ----- Chapter CRUD ----- */
  const openNewChapter = () => { setChapterForm({ chapter_number: "", title: "", is_published: true }); setEditingChapterId(null); setChapterDialog(true); };
  const openEditChapter = (c) => {
    setChapterForm({ chapter_number: String(c.chapter_number), title: c.title, is_published: c.is_published });
    setEditingChapterId(c.id);
    setChapterDialog(true);
  };
  const saveChapter = async () => {
    const num = parseFloat(chapterForm.chapter_number);
    if (isNaN(num)) return toast.error("Valid chapter number required (e.g. 1 or 1.5)");
    setSavingChapter(true);
    const payload = { series_id: selectedSeries.id, chapter_number: num, title: chapterForm.title, is_published: chapterForm.is_published };
    try {
      if (editingChapterId) {
        await api.put(`/admin/chapters/${editingChapterId}`, payload);
        toast.success("Chapter updated");
      } else {
        await api.post("/admin/chapters", payload);
        toast.success("Chapter created");
      }
      setChapterDialog(false);
      await refreshChapters(selectedSeries.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingChapter(false);
    }
  };
  const togglePublish = async (c) => {
    try {
      await api.put(`/admin/chapters/${c.id}`, { series_id: selectedSeries.id, chapter_number: c.chapter_number, title: c.title, is_published: !c.is_published });
      toast.success(!c.is_published ? "Chapter published" : "Chapter unpublished");
      refreshChapters(selectedSeries.id);
    } catch { toast.error("Update failed"); }
  };
  const deleteChapter = async (c) => {
    if (!window.confirm(`Delete chapter ${c.chapter_number}?`)) return;
    try {
      await api.delete(`/admin/chapters/${c.id}`);
      toast.success("Chapter deleted");
      if (selectedChapter?.id === c.id) { setSelectedChapter(null); setPanels([]); }
      refreshChapters(selectedSeries.id);
    } catch { toast.error("Delete failed"); }
  };

  /* ----- Panel management ----- */
  const uploadPanels = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPanel(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/admin/panels", { chapter_id: selectedChapter.id, image_url: data.url, alt_text: file.name });
      }
      toast.success(`${files.length} panel(s) added`);
      const { data } = await api.get(`/admin/chapters/${selectedChapter.id}/panels`);
      setPanels(data);
      refreshChapters(selectedSeries.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingPanel(false);
      if (panelInputRef.current) panelInputRef.current.value = "";
    }
  };
  const movePanel = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= panels.length) return;
    const reordered = [...panels];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setPanels(reordered);
    try {
      const { data } = await api.post("/admin/panels/reorder", { chapter_id: selectedChapter.id, panel_ids: reordered.map((p) => p.id) });
      setPanels(data);
    } catch { toast.error("Reorder failed"); }
  };
  const replacePanel = async (p, file) => {
    if (!file) return;
    setReplacingId(p.id);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: updated } = await api.put(`/admin/panels/${p.id}/image`, { image_url: data.url });
      setPanels((list) => list.map((x) => (x.id === p.id ? updated : x)));
      toast.success("Panel image replaced");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Replace failed");
    } finally {
      setReplacingId(null);
    }
  };
  const deletePanel = async (p) => {
    try {
      await api.delete(`/admin/panels/${p.id}`);
      const { data } = await api.get(`/admin/chapters/${selectedChapter.id}/panels`);
      setPanels(data);
      refreshChapters(selectedSeries.id);
      toast.success("Panel removed");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-gray-200">
      {/* Top bar */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-rose-600 to-violet-600 flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-extrabold text-lg text-white">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="admin-subscribers-btn" onClick={() => setSubsOpen(true)} variant="ghost" className="text-gray-300 hover:bg-white/5">
              <Mail className="w-4 h-4 mr-1.5" /> Subscribers
            </Button>
            <SubscribersDialog open={subsOpen} onOpenChange={setSubsOpen} />
            <Button data-testid="admin-view-site-btn" onClick={() => navigate("/")} variant="ghost" className="text-gray-300 hover:bg-white/5">
              <Eye className="w-4 h-4 mr-1.5" /> View Site
            </Button>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/admin/login"); }} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5">
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-6">
        {/* Series list column */}
        <aside className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-bold text-lg text-white flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-rose-500" /> Series ({seriesList.length})
            </h2>
            <Button data-testid="new-series-btn" onClick={openNewSeries} size="sm" className="bg-rose-600 hover:bg-rose-500 text-white">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          </div>
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
          ) : (
            <div className="space-y-2">
              {seriesList.map((s) => (
                <div
                  key={s.id}
                  data-testid={`admin-series-${s.slug}`}
                  onClick={() => openSeriesDetail(s)}
                  className={`cursor-pointer rounded-xl border p-3 flex items-center gap-3 transition-all ${
                    selectedSeries?.id === s.id ? "border-rose-500/50 bg-[#1A1D2B]" : "border-white/10 bg-[#12141D] hover:border-white/20"
                  }`}
                >
                  <div className="w-11 h-14 rounded-md overflow-hidden bg-black/40 shrink-0">
                    {s.cover_image_url && <img src={resolveImg(s.cover_image_url)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-medium text-sm truncate">{s.title}</p>
                    <p className="text-xs text-gray-500 font-mono-kh mt-0.5">{s.published_chapters}/{s.total_chapters} published</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <StatusBadge status={s.status} />
                      <span
                        data-testid={`series-draft-badge-${s.slug}`}
                        className={`font-mono-kh text-[9px] uppercase tracking-widest border px-1.5 py-0.5 rounded-full ${
                          s.is_published
                            ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                            : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                        }`}
                      >
                        {s.is_published ? "Live" : "Draft"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </aside>

        {/* Detail column */}
        <main>
          {!selectedSeries ? (
            <div className="h-full min-h-[400px] flex flex-col items-center justify-center text-center border border-dashed border-white/10 rounded-2xl">
              <Layers className="w-10 h-10 text-gray-700 mb-3" />
              <p className="text-gray-500">Select a series to manage its chapters & panels</p>
            </div>
          ) : selectedChapter ? (
            /* Panel manager */
            <div>
              <button data-testid="panels-back-btn" onClick={() => { setSelectedChapter(null); setPanels([]); }} className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4">
                <ChevronLeft className="w-4 h-4" /> Back to chapters
              </button>
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <h2 className="font-display font-bold text-xl text-white">
                    {selectedSeries.title} · Ch {selectedChapter.chapter_number}
                  </h2>
                  <p className="text-sm text-gray-500">{panels.length} panels (top → bottom reading order)</p>
                </div>
                <input ref={panelInputRef} type="file" accept="image/*" multiple hidden onChange={uploadPanels} data-testid="panel-upload-input" />
                <Button data-testid="add-panels-btn" onClick={() => panelInputRef.current?.click()} disabled={uploadingPanel} className="bg-rose-600 hover:bg-rose-500 text-white">
                  {uploadingPanel ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Add Panels
                </Button>
              </div>

              {panels.length === 0 ? (
                <div className="py-16 text-center border border-dashed border-white/10 rounded-2xl text-gray-500">
                  No panels yet. Upload image slices to build the chapter.
                </div>
              ) : (
                <div className="space-y-2">
                  {panels.map((p, i) => (
                    <div key={p.id} data-testid={`admin-panel-${i}`} className="flex items-center gap-3 bg-[#12141D] border border-white/10 rounded-xl p-2.5">
                      <span className="font-display font-bold text-rose-500 w-7 text-center shrink-0">{p.panel_order}</span>
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-black/40 shrink-0">
                        <img src={resolveImg(p.image_url)} alt={p.alt_text} className="w-full h-full object-cover" />
                      </div>
                      <span className="text-xs text-gray-500 truncate flex-1">{p.alt_text}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button data-testid={`panel-up-${i}`} onClick={() => movePanel(i, -1)} disabled={i === 0} size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-white disabled:opacity-30"><ArrowUp className="w-4 h-4" /></Button>
                        <Button data-testid={`panel-down-${i}`} onClick={() => movePanel(i, 1)} disabled={i === panels.length - 1} size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:text-white disabled:opacity-30"><ArrowDown className="w-4 h-4" /></Button>
                        <label data-testid={`panel-replace-${i}`} title="Replace image" className="h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-[#FF7A1A] hover:bg-white/5 cursor-pointer">
                          {replacingId === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          <input type="file" accept="image/*" hidden disabled={!!replacingId} onChange={(e) => { replacePanel(p, e.target.files?.[0]); e.target.value = ""; }} />
                        </label>
                        <Button data-testid={`panel-delete-${i}`} onClick={() => deletePanel(p)} size="icon" variant="ghost" className="h-8 w-8 text-rose-500 hover:text-rose-400 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Chapter manager */
            <div>
              <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-20 rounded-lg overflow-hidden bg-black/40 shrink-0">
                    {selectedSeries.cover_image_url && <img src={resolveImg(selectedSeries.cover_image_url)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div>
                    <h2 className="font-display font-bold text-2xl text-white">{selectedSeries.title}</h2>
                    <div className="flex items-center gap-2 mt-1.5">
                      <StatusBadge status={selectedSeries.status} />
                      <span className="text-xs text-gray-500 font-mono-kh">{selectedSeries.genre}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-2 bg-[#12141D] border border-white/10 rounded-full px-3 py-1.5">
                    <span className={`font-mono-kh text-[10px] uppercase tracking-widest ${selectedSeries.is_published ? "text-emerald-400" : "text-amber-400"}`}>
                      {selectedSeries.is_published ? "Live" : "Draft"}
                    </span>
                    <Switch
                      data-testid="series-publish-toggle"
                      checked={!!selectedSeries.is_published}
                      onCheckedChange={() => toggleSeriesPublish(selectedSeries)}
                      className="data-[state=checked]:bg-emerald-600"
                    />
                  </div>
                  <Button data-testid="feature-series-btn" disabled={!!selectedSeries.is_featured} onClick={() => featureSeries(selectedSeries)} variant="outline" className="border-[#FF7A1A]/40 bg-transparent text-[#FF7A1A] hover:bg-[#FF7A1A]/10 disabled:opacity-100"><Star className="w-4 h-4 mr-1.5" /> {selectedSeries.is_featured ? "Featured" : "Feature on homepage"}</Button>
                  <Button data-testid="preview-series-btn" onClick={() => openPreview(chapters[0]?.chapter_number)} variant="outline" className="border-violet-500/40 bg-transparent text-violet-300 hover:bg-violet-500/10"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>
                  <Button data-testid="edit-series-btn" onClick={() => openEditSeries(selectedSeries)} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>
                  <Button data-testid="delete-series-btn" onClick={() => deleteSeries(selectedSeries)} variant="outline" className="border-rose-500/30 bg-transparent text-rose-400 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>

              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-lg text-white">Chapters ({chapters.length})</h3>
                <Button data-testid="new-chapter-btn" onClick={openNewChapter} size="sm" className="bg-rose-600 hover:bg-rose-500 text-white"><Plus className="w-4 h-4 mr-1" /> Add Chapter</Button>
              </div>

              {chapters.length === 0 ? (
                <div className="py-14 text-center border border-dashed border-white/10 rounded-2xl text-gray-500">No chapters. Add one (supports decimals like 1.5).</div>
              ) : (
                <div className="space-y-2">
                  {chapters.map((c) => (
                    <div key={c.id} data-testid={`admin-chapter-${c.chapter_number}`} className="flex items-center gap-3 bg-[#12141D] border border-white/10 rounded-xl px-4 py-3">
                      <span className="font-display font-bold text-rose-500 text-lg w-12 shrink-0">{c.chapter_number}</span>
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openChapterPanels(c)}>
                        <p className="text-white font-medium truncate">{c.title || `Chapter ${c.chapter_number}`}</p>
                        <p className="text-xs text-gray-500 font-mono-kh">{c.panel_count} panels</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-2">
                          <Switch data-testid={`chapter-publish-toggle-${c.chapter_number}`} checked={c.is_published} onCheckedChange={() => togglePublish(c)} className="data-[state=checked]:bg-emerald-600" />
                          <span className="text-xs text-gray-500 hidden sm:inline w-16">{c.is_published ? "Published" : "Draft"}</span>
                        </div>
                        <Button data-testid={`manage-panels-${c.chapter_number}`} onClick={() => openChapterPanels(c)} size="sm" variant="outline" className="border-white/15 bg-transparent hover:bg-white/5"><ImageIcon className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Panels</span></Button>
                        <Button data-testid={`preview-chapter-${c.chapter_number}`} onClick={() => openPreview(c.chapter_number)} size="icon" variant="ghost" className="h-9 w-9 text-violet-300 hover:bg-violet-500/10" title="Preview"><Eye className="w-4 h-4" /></Button>
                        <Button data-testid={`edit-chapter-${c.chapter_number}`} onClick={() => openEditChapter(c)} size="icon" variant="ghost" className="h-9 w-9 text-gray-400 hover:text-white"><Pencil className="w-4 h-4" /></Button>
                        <Button data-testid={`delete-chapter-${c.chapter_number}`} onClick={() => deleteChapter(c)} size="icon" variant="ghost" className="h-9 w-9 text-rose-500 hover:bg-rose-500/10"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Series dialog */}
      <Dialog open={seriesDialog} onOpenChange={setSeriesDialog}>
        <DialogContent className="bg-[#12141D] border-white/10 text-gray-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-white">{editingSeriesId ? "Edit Series" : "New Series"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-gray-300 text-sm">Title *</Label>
              <Input data-testid="series-title-input" value={seriesForm.title} onChange={(e) => setSeriesForm({ ...seriesForm, title: e.target.value })} className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300 text-sm">Genre</Label>
                <Input data-testid="series-genre-input" value={seriesForm.genre} onChange={(e) => setSeriesForm({ ...seriesForm, genre: e.target.value })} placeholder="Cyberpunk / Action" className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
              </div>
              <div>
                <Label className="text-gray-300 text-sm">Author</Label>
                <Input data-testid="series-author-input" value={seriesForm.author} onChange={(e) => setSeriesForm({ ...seriesForm, author: e.target.value })} className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Synopsis</Label>
              <Textarea data-testid="series-synopsis-input" value={seriesForm.synopsis} onChange={(e) => setSeriesForm({ ...seriesForm, synopsis: e.target.value })} rows={3} className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Homepage featured tagline (optional)</Label>
              <Textarea data-testid="series-featured-tagline-input" value={seriesForm.featured_tagline} onChange={(e) => setSeriesForm({ ...seriesForm, featured_tagline: e.target.value })} rows={2} placeholder="Shown on the homepage when this series is featured. Leave blank to use the synopsis." className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Status</Label>
              <Select value={seriesForm.status} onValueChange={(v) => setSeriesForm({ ...seriesForm, status: v })}>
                <SelectTrigger data-testid="series-status-select" className="mt-1.5 bg-[#0B0C10] border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#12141D] border-white/10 text-gray-200">
                  <SelectItem value="Ongoing">Ongoing</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Hiatus">Hiatus</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ImageUploadField label="Cover Image" value={seriesForm.cover_image_url} onChange={(url) => setSeriesForm({ ...seriesForm, cover_image_url: url })} testid="series-cover-upload" />
            <ImageUploadField label="Banner Image" value={seriesForm.banner_image_url} onChange={(url) => setSeriesForm({ ...seriesForm, banner_image_url: url })} testid="series-banner-upload" />
          </div>
          <DialogFooter>
            <Button data-testid="save-series-btn" onClick={saveSeries} disabled={savingSeries} className="bg-rose-600 hover:bg-rose-500 text-white w-full sm:w-auto">
              {savingSeries ? <Loader2 className="w-4 h-4 animate-spin" /> : editingSeriesId ? "Save Changes" : "Create Series"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chapter dialog */}
      <Dialog open={chapterDialog} onOpenChange={setChapterDialog}>
        <DialogContent className="bg-[#12141D] border-white/10 text-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-white">{editingChapterId ? "Edit Chapter" : "New Chapter"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-gray-300 text-sm">Chapter Number * <span className="text-gray-600 font-mono-kh text-xs">(decimals allowed, e.g. 1.5)</span></Label>
              <Input data-testid="chapter-number-input" type="number" step="0.1" value={chapterForm.chapter_number} onChange={(e) => setChapterForm({ ...chapterForm, chapter_number: e.target.value })} placeholder="1.0" className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Title (optional)</Label>
              <Input data-testid="chapter-title-input" value={chapterForm.title} onChange={(e) => setChapterForm({ ...chapterForm, title: e.target.value })} className="mt-1.5 bg-[#0B0C10] border-white/10 text-white focus-visible:ring-rose-500" />
            </div>
            <div className="flex items-center justify-between bg-[#0B0C10] border border-white/10 rounded-lg px-4 py-3">
              <Label className="text-gray-300 text-sm">Published</Label>
              <Switch data-testid="chapter-published-switch" checked={chapterForm.is_published} onCheckedChange={(v) => setChapterForm({ ...chapterForm, is_published: v })} className="data-[state=checked]:bg-emerald-600" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-chapter-btn" onClick={saveChapter} disabled={savingChapter} className="bg-rose-600 hover:bg-rose-500 text-white w-full sm:w-auto">
              {savingChapter ? <Loader2 className="w-4 h-4 animate-spin" /> : editingChapterId ? "Save Changes" : "Create Chapter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
